// @ts-strict-ignore
import { useTranslation } from "react-i18next";

import { Message } from "@aviary";
import { l } from "@shared/locales/i18n";
import { AvailabilityStatus } from "@shared/types/availabilityStatus";
import type { Variant } from "@shared/types/graphqlGenerated";
import { getExpectedReturnDate } from "@shared/utils/getExpectedReturnDate/getExpectedReturnDate";

interface Props {
  availabilityStatus: AvailabilityStatus | string;
  expectedReturnDate: Variant["expectedReturnDate"];
}

const ProductAvailabilityMessage = ({ availabilityStatus, expectedReturnDate }: Props) => {
  const { t } = useTranslation();
  const { isValid, returnDate, daysToReturn } = getExpectedReturnDate(expectedReturnDate);

  const renderReturnDateContent = label => (
    <>
      {label} <b>{returnDate}</b> {t(l.variantStatus.DaysToReturn, { count: daysToReturn })}
    </>
  );

  const renderOutOfStockMessage = () => {
    if (isValid) {
      return (
        <Message isColor="danger">
          {renderReturnDateContent(t(l.variantStatus.OutOfStockWithReturnDate))}
        </Message>
      );
    }

    return <Message isColor="danger">{t(l.variantStatus.OutOfStockWithNoReturnDate)}</Message>;
  };

  const renderBackorderedMessage = () => {
    if (isValid) {
      return (
        <Message isColor="warning">
          {renderReturnDateContent(t(l.variantStatus.BackorderedWithReturnDate))}
        </Message>
      );
    }

    return (
      <Message isColor="warning">{t(l.variantStatus.wholesaleBackorderedWithNoReturnDate)}</Message>
    );
  };

  if (availabilityStatus === AvailabilityStatus.BACKORDERED) {
    return renderBackorderedMessage();
  }

  if (availabilityStatus === AvailabilityStatus.UNAVAILABLE) {
    return renderOutOfStockMessage();
  }

  if (availabilityStatus === AvailabilityStatus.NA) {
    return renderOutOfStockMessage();
  }

  return null;
};

export { ProductAvailabilityMessage };
